import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, FileText } from "lucide-react";
import { PageHeader } from "../components/sections/Shared";
import Seo from "../components/Seo";
import { COMPANY } from "../data/mock";

const SECTIONS = [
  {
    title: "1. Acceptance of terms",
    body:
      "These Conditions govern services provided by OBLED Corporation d/b/a 518 Courier (\"518 Courier\", \"we\", \"us\"). By accessing or using our services (the \"Service\"), you agree to be bound by these Conditions. If you do not agree, please do not use the Service."
  },
  {
    title: "2. Services provided",
    body:
      "518 Courier provides same-day, scheduled, medical, business and last-mile courier and delivery services across the Capital Region of New York, and interstate courier service between New York, Massachusetts, Connecticut and Vermont. All deliveries are subject to scheduling, driver availability, and applicable federal and New York State laws. 518 Courier transports packages, documents and medical materials — not patients or passengers."
  },
  {
    title: "3. Booking and quotes",
    body:
      "Quotes are estimates based on distance, weight and service level. Final pricing is confirmed by dispatch before driver assignment. Cancellations after driver dispatch may incur a minimum service charge."
  },
  {
    title: "4. Insurance and liability",
    body:
      "Insurance and coverage details, including certificates of insurance, are available on request for business accounts. We are not liable for delays caused by force majeure, weather, or recipient unavailability. Liability for loss or damage is limited as described at booking and subject to applicable law."
  },
  {
    title: "5. Prohibited items",
    body:
      "You may not ship: hazardous materials, illegal substances, firearms, live animals, perishables exceeding 4 hours of transit, currency or precious metals exceeding $1,000, or anything prohibited under federal or state law."
  },
  {
    title: "6. Medical shipments",
    body:
      "518 Courier follows HIPAA-aligned privacy, security and chain-of-custody procedures for medical courier operations. Medical shipments are handled only by trained and authorized personnel. Formal Business Associate Agreements and supporting documentation are available when required by the client. Special handling requirements should be confirmed with dispatch at booking."
  },
  {
    title: "7. Account & access",
    body:
      "You are responsible for maintaining the confidentiality of your account credentials. Notify us immediately of any unauthorized use. We reserve the right to suspend accounts for misuse."
  },
  {
    title: "8. Driver applications",
    body:
      "All driver applicants consent to a motor vehicle record check, background check, and identity verification. Employment is at-will and subject to applicable state and federal law."
  },
  {
    title: "9. Privacy",
    body:
      "We collect only the information necessary to deliver your shipments. We do not sell personal data. See our Privacy notice for full details."
  },
  {
    title: "10. Changes to these terms",
    body:
      "We may update these Conditions from time to time. Continued use of the Service after changes constitutes acceptance of the updated terms."
  },
  {
    title: "11. Contact",
    body: `Questions about these Conditions? Contact us at ${COMPANY.email} or ${COMPANY.phone}. Mailing address: ${COMPANY.address}.`
  }
];

export default function Conditions() {
  return (
    <>
      <Seo
        title="Terms & Conditions | 518 Courier"
        description="518 Courier terms and conditions covering courier service bookings, delivery, insurance, prohibited items and customer accounts."
        path="/conditions"
      />
      <PageHeader
        eyebrow="Legal"
        title="Terms &"
        accent="Conditions."
        description="The fine print, in plain English. OBLED Corporation d/b/a 518 Courier. Last updated June 2026."
      />
      <section className="py-16 md:py-20 bg-white">
        <div className="container-518 grid lg:grid-cols-12 gap-10">
          {/* TOC */}
          <aside className="lg:col-span-3 lg:sticky lg:top-28 self-start h-fit">
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4 text-[#f59321]" />
                <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-500">Contents</span>
              </div>
              <ol className="space-y-2">
                {SECTIONS.map((s, i) => (
                  <li key={i}>
                    <a
                      href={`#sec-${i}`}
                      className="flex items-start gap-1.5 text-sm text-slate-600 hover:text-[#f59321] font-medium"
                    >
                      <ChevronRight className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      {s.title}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          <article className="lg:col-span-9 max-w-3xl">
            {SECTIONS.map((s, i) => (
              <div key={i} id={`sec-${i}`} className="mb-10 scroll-mt-28">
                <h2 className="font-display text-2xl md:text-3xl font-extrabold text-[#0f2847] mb-3">
                  {s.title}
                </h2>
                <p className="text-slate-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
            <div className="mt-12 p-5 rounded-2xl bg-slate-50 border border-slate-200 text-sm text-slate-600">
              By creating an account or booking a delivery, you confirm that you have read and agree to these Conditions.
              <Link to="/contact" className="ml-1 text-[#f59321] font-semibold hover:underline">Questions? Contact us.</Link>
            </div>
          </article>
        </div>
      </section>
    </>
  );
}
